import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  RowView,
} from "../../../../Component/style/common_style";
import { useNavigate } from "react-router-dom";
import api from "../../../../Component/axios_Interceptor/api";
import url from "../../../../Component/server";
import { useAppearance } from "../../../../Component/UI/useAppearance";
import { AttentionDot } from '../OrderHistory';

interface handleSidebarToggleProps {
  handleSidebarToggle: () => void;
  isVisible: boolean;
  isLogin: boolean;
  onLogout: () => void;
  hasOrderAttention?: boolean;
  orderAttentionError?: boolean;
}

const SideBar = ({
  handleSidebarToggle,
  isVisible,
  isLogin,
  onLogout,
  hasOrderAttention = false,
  orderAttentionError = false,
}: handleSidebarToggleProps) => {
  const navigate = useNavigate();
  const { appearance, toggleAppearance } = useAppearance();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!isLogin || !isVisible) return;
    let active = true;
    api.get(url.Communication_url.support_unread_count).then(response => {
      if (active) setUnreadCount(Number(response.data?.unread_count || 0));
    }).catch(() => { if (active) setUnreadCount(0); });
    return () => { active = false; };
  }, [isLogin, isVisible]);

  const goServiceCenter = () => {
    navigate("/help");
  };

  const goMyInfo = () => {
    navigate("/my-info");
  };

  return (
    <>
      {isVisible && <Backdrop type="button" aria-label="메뉴 바깥 영역을 눌러 닫기" tabIndex={-1} onClick={handleSidebarToggle} />}
    <SideBarDiv $isVisible={isVisible} aria-label="고객 메뉴" aria-hidden={!isVisible}>
      <TitleDiv>
        <div>메뉴</div>
        <CloseButton type="button" aria-label="메뉴 닫기" onClick={handleSidebarToggle}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
        </CloseButton>
      </TitleDiv>
      <MenuList>
        {isLogin && (
          <MenuGroup>
            <CategoryTitle>계정</CategoryTitle>
            <MenuItem type="button" onClick={goMyInfo}>
              내 정보
            </MenuItem>
            <MenuItem type="button" onClick={() => navigate('/orders')}>
              주문 내역 {hasOrderAttention && <AttentionDot role="img" aria-label="입금 기한 확인이 필요한 주문이 있습니다" />}
            </MenuItem>
            <MenuItem type="button" onClick={() => { handleSidebarToggle(); navigate('/my-notifications'); }}>알림 설정</MenuItem>
            {orderAttentionError && <OrderAlertError role="status">입금 알림을 확인하지 못했습니다. 주문 내역에서 다시 확인해주세요.</OrderAlertError>}
          </MenuGroup>
        )}
        <MenuGroup>
          <CategoryTitle>서비스</CategoryTitle>
          <MenuItem type="button" onClick={goServiceCenter}>
            고객센터
            {unreadCount > 0 && <UnreadBadge aria-label={`읽지 않은 상담 메시지 ${unreadCount}개`}>{unreadCount > 99 ? "99+" : unreadCount}</UnreadBadge>}
          </MenuItem>
        </MenuGroup>
      </MenuList>
      <BottomArea>
        <ThemeToggle type="button" role="switch" aria-label="다크 모드" aria-checked={appearance === 'dark'} onClick={toggleAppearance}>
          <span><svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 15.3A8.5 8.5 0 0 1 8.7 4a8.5 8.5 0 1 0 11.3 11.3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>다크 모드</span>
          <SwitchTrack $on={appearance === 'dark'} aria-hidden="true"><span /></SwitchTrack>
        </ThemeToggle>
        {isLogin && <LogoutButton type="button" onClick={onLogout}>로그아웃</LogoutButton>}
      </BottomArea>
    </SideBarDiv>
    </>
  );
};
export default SideBar;

// 슬라이드 애니메이션 정의
const slideIn = keyframes`
  from {
    transform: translateX(100%); /* 화면 밖에서 시작 */
  }
  to {
    transform: translateX(0); /* 화면 안으로 이동 */
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0); /* 화면 안에서 시작 */
  }
  to {
    transform: translateX(100%); /* 화면 밖으로 이동 */
  }
`;

const Backdrop = styled.button`
  position: fixed;
  inset: 0;
  z-index: 100;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: default;
  -webkit-tap-highlight-color: transparent;
`;

const SideBarDiv = styled.aside<{ $isVisible: boolean }>`

  width: min(calc(var(--browser-layout-width, 100vw) * .8), 280px);
  max-width: 280px;
  height: var(--browser-layout-height, 100dvh);
  top: 0;
  right: max(0px, calc((var(--browser-layout-width, 100vw) - 500px) / 2));
  box-sizing: border-box;
  background-color: var(--user-surface);
  color: var(--user-text);
  border-left: 1px solid var(--user-border);
  box-shadow: -12px 0 40px #29243312;
  position: fixed;
  display: flex;
  flex-direction: column;
  z-index: 101;  visibility: ${({ $isVisible }) => $isVisible ? 'visible' : 'hidden'};
  pointer-events: ${({ $isVisible }) => $isVisible ? 'auto' : 'none'};
  transition: visibility 0s ${({ $isVisible }) => $isVisible ? '0s' : '0.3s'};

  animation: ${({ $isVisible }) => ($isVisible ? slideIn : slideOut)} 0.3s
    ease-in-out;
  transform: ${({ $isVisible }) =>
    $isVisible ? "translateX(0)" : "translateX(100%)"};
`;

const TitleDiv = styled(RowView)`
  padding: 24px;
  font-family: var(--font-Pretendard-SemiBold);
  font-size: 18px;
`;

const MenuList = styled.div`
  padding: 12px 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  gap: 28px;
`;

const MenuGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CategoryTitle = styled.div`
  color: var(--user-text-muted);
  font-size: 13px;
  font-family: var(--font-Pretendard-Regular);
`;

const MenuItem = styled.button`
  width: fit-content;
  min-height: 44px;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: transparent;
  color: var(--user-text);
  font-size: 18px;
  text-align: left;
  font-family: var(--font-Pretendard-SemiBold);
  &:active {
    opacity: 0.8;
  }
`;

const UnreadBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  margin-left: 8px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 10px;
  background: #d92d20;
  color: #ffffff;
  font-size: 11px;
`;

const OrderAlertError = styled.p`margin:0;color:var(--user-error);font-size:12px;line-height:1.5;`;

const BottomArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
  padding: 20px 24px calc(24px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--user-border);
`;

const LogoutButton = styled.button`
  width: 100%;
  min-height: 44px;
  border: 1px solid var(--user-border);
  border-radius: 8px;
  background: transparent;
  color: var(--user-text);
  font-size: 15px;
  font-family: var(--font-Pretendard-SemiBold);
  cursor: pointer;
  &:hover { background: var(--user-surface-raised); }
  &:focus-visible { outline: 3px solid #a78cff; outline-offset: 2px; }
`;

const CloseButton = styled.button`
  display: grid; place-items: center; width: 44px; height: 44px; margin: -10px -10px -10px 0;
  border: 0; border-radius: 8px; background: transparent; color: inherit; cursor: pointer;
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
`;
const ThemeToggle = styled.button`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  width: 100%; min-height: 48px; padding: 0; border: 0; border-radius: 6px;
  background: transparent; color: var(--user-text); font: 15px var(--font-Pretendard-Medium); cursor: pointer;
  > span:first-child { display: flex; align-items: center; gap: 9px; white-space: nowrap; }
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 4px; }
`;
const SwitchTrack = styled.span<{ $on: boolean }>`
  flex-shrink: 0; display: flex; align-items: center; box-sizing: border-box; width: 42px; height: 26px; padding: 3px;
  border: 1px solid var(--user-border); border-radius: 20px; background: ${({ $on }) => $on ? 'var(--user-button)' : 'var(--user-surface-raised)'};
  span { display: block; width: 18px; height: 18px; border-radius: 50%; background: ${({ $on }) => $on ? '#fff' : 'var(--user-text-muted)'}; transform: translateX(${({ $on }) => $on ? '16px' : '0'}); }
`;

